// Browser platform adapter — maps zr-webrtc's PlatformAdapter onto standard DOM WebRTC.

// The native glassmorphism call UI kit (WhatsApp / Google Meet experiences).
export { mountCallUI } from './callUI';
export type { CallUIClient, CallUIOptions, CallUIHandle } from './callUI';

import {
  cssFilterFor,
  type PlatformAdapter,
  type PeerConnectionOptions,
  type IceConfig,
  type ScreenComposite,
  type ScreenCompositeOptions,
  type VideoEffectPipeline,
  type VideoEffect,
} from 'zr-webrtc';

/**
 * Canvas-based camera effect pipeline: draws each camera frame through a CSS/canvas
 * `filter` (blur, mono, sepia, vivid, warm/cool, background-blur) and re-exposes the
 * canvas as a MediaStream track via captureStream(). The processed track is published to
 * peers, so the effect is visible remotely — not just a local CSS preview. Image-based
 * background replacement needs a segmentation model, which an app can layer on top.
 */
class CanvasVideoEffect implements VideoEffectPipeline {
  readonly track: MediaStreamTrack;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private video: HTMLVideoElement;
  private stream: MediaStream;
  private raf = 0;

  constructor(source: MediaStreamTrack, effect: VideoEffect) {
    const settings = (source.getSettings?.() ?? {}) as MediaTrackSettings;
    const w = settings.width || 640;
    const h = settings.height || 480;
    const fps = settings.frameRate || 24;
    const filter = cssFilterFor(effect);

    this.canvas = document.createElement('canvas');
    this.canvas.width = w;
    this.canvas.height = h;
    this.ctx = this.canvas.getContext('2d')!;

    this.video = document.createElement('video');
    this.video.srcObject = new MediaStream([source]);
    this.video.muted = true;
    this.video.playsInline = true;
    void this.video.play().catch(() => {});

    const draw = () => {
      this.raf = requestAnimationFrame(draw);
      try {
        this.ctx.filter = filter;
        this.ctx.drawImage(this.video, 0, 0, w, h);
      } catch {
        /* first frames may not be decodable yet */
      }
    };
    draw();

    this.stream = this.canvas.captureStream(fps);
    this.track = this.stream.getVideoTracks()[0];
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    this.video.srcObject = null;
    this.stream.getTracks().forEach((t) => t.stop());
  }
}

/**
 * Canvas-based compositor: draws the screen share full-frame and the presenter's
 * camera as a circular bubble in a corner, then exposes the canvas as a MediaStream
 * via captureStream(). Publishing this single stream gives every participant the
 * StreamYard-style "presenter face on the shared screen" view.
 */
class CanvasScreenComposite implements ScreenComposite {
  readonly stream: MediaStream;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private screenVideo: HTMLVideoElement;
  private camVideo?: HTMLVideoElement;
  private cameraTrack: MediaStreamTrack | null = null;
  private raf = 0;

  constructor(screen: MediaStream, camera: MediaStream | null, opts: ScreenCompositeOptions = {}) {
    const fps = opts.fps ?? 24;
    const settings = (screen.getVideoTracks()[0]?.getSettings?.() ?? {}) as MediaTrackSettings;
    const w = settings.width || 1280;
    const h = settings.height || 720;

    this.canvas = document.createElement('canvas');
    this.canvas.width = w;
    this.canvas.height = h;
    this.ctx = this.canvas.getContext('2d')!;

    this.screenVideo = document.createElement('video');
    this.screenVideo.srcObject = screen;
    this.screenVideo.muted = true;
    this.screenVideo.playsInline = true;
    void this.screenVideo.play().catch(() => {});

    if (camera && camera.getVideoTracks().length) {
      this.cameraTrack = camera.getVideoTracks()[0];
      this.camVideo = document.createElement('video');
      this.camVideo.srcObject = new MediaStream(camera.getVideoTracks());
      this.camVideo.muted = true;
      this.camVideo.playsInline = true;
      void this.camVideo.play().catch(() => {});
    }

    const diameter = (opts.diameterRatio ?? 0.22) * h;
    const radius = diameter / 2;
    const margin = opts.margin ?? Math.round(h * 0.04);
    const ringColor = opts.ringColor ?? '#ffffff';
    const ringWidth = opts.ringWidth ?? Math.max(3, Math.round(diameter * 0.025));
    const position = opts.position ?? 'bottom-right';

    const draw = () => {
      this.raf = requestAnimationFrame(draw);
      const ctx = this.ctx;

      // Screen, scaled to fill the canvas.
      try {
        ctx.drawImage(this.screenVideo, 0, 0, w, h);
      } catch {
        /* first frames may not be decodable yet */
      }

      // Circular self-cam bubble — drawn only while the camera is enabled. Turning the
      // camera off drops just the bubble; the screen keeps sharing full-frame.
      if (this.camVideo && this.camVideo.readyState >= 2 && this.cameraTrack?.enabled) {
        let cx = w - margin - radius;
        let cy = h - margin - radius;
        if (position.includes('left')) cx = margin + radius;
        if (position.includes('top')) cy = margin + radius;

        // Center-crop the camera frame to a square so the circle isn't distorted.
        const cw = this.camVideo.videoWidth || 640;
        const ch = this.camVideo.videoHeight || 480;
        const side = Math.min(cw, ch);
        const sx = (cw - side) / 2;
        const sy = (ch - side) / 2;

        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(this.camVideo, sx, sy, side, side, cx - radius, cy - radius, diameter, diameter);
        ctx.restore();

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.lineWidth = ringWidth;
        ctx.strokeStyle = ringColor;
        ctx.stroke();
      }
    };
    draw();

    this.stream = this.canvas.captureStream(fps);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    this.screenVideo.srcObject = null;
    if (this.camVideo) this.camVideo.srcObject = null;
    this.stream.getTracks().forEach((t) => t.stop());
  }
}

export const webPlatform: PlatformAdapter = {
  createPeerConnection(config: IceConfig, remoteId?: string, selfId?: string, opts?: PeerConnectionOptions): RTCPeerConnection {
    const rtcConfig: RTCConfiguration = {
      iceServers: config.iceServers,
      // Pre-gather candidates (incl. UDP relay) so a UDP path is ready before offer/answer,
      // rather than trickling in slowly and letting the TCP/TLS relay win first.
      iceCandidatePoolSize: 8,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      // 'all' (default) tries direct P2P over UDP first, relaying only when needed — the path
      // that yields UDP most often. Developers can force 'relay' for locked-down networks.
      iceTransportPolicy: opts?.iceTransportPolicy ?? 'all',
    };
    // Opt into Insertable Streams ONLY when E2EE is active. Enabling it without pumping the
    // encoded streams makes Chrome/Edge hold every frame → 0 bytes sent → a silent "connected
    // but no media" call. When E2EE is on, zr-webrtc attaches the encrypt/decrypt transform.
    if (opts?.encodedInsertableStreams) {
      (rtcConfig as any).encodedInsertableStreams = true;
    }
    return new RTCPeerConnection(rtcConfig);
  },
  getUserMedia(constraints): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia(constraints);
  },
  getDisplayMedia(): Promise<MediaStream> {
    return navigator.mediaDevices.getDisplayMedia({ video: true });
  },
  createMediaStream(tracks?: MediaStreamTrack[]): MediaStream {
    return tracks ? new MediaStream(tracks) : new MediaStream();
  },
  createScreenComposite(screen, camera, opts): ScreenComposite {
    return new CanvasScreenComposite(screen, camera, opts);
  },
  createVideoEffectPipeline(source: MediaStreamTrack, effect: VideoEffect): VideoEffectPipeline {
    return new CanvasVideoEffect(source, effect);
  },
};
